import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery } from "@tanstack/react-query";
import { ModuleShell, StatTile } from "@/components/intel/ModuleShell";
import { AppLayout } from "@/components/intel/Layout";
import { fetchSourceHealth } from "@/lib/intel/feeds.functions";
import { getFabricOverview, validateFabricSources } from "@/lib/intel/fabric/fabric.functions";
import { Database, Search, RefreshCw, ShieldCheck, Activity, KeyRound } from "lucide-react";

export const Route = createFileRoute("/sources")({
  head: () => ({
    meta: [
      { title: "Source Registry · CARIO NEXUS" },
      { name: "description", content: "Browse every public data source CARIO can query: provider, country, protocol, access class and live health." },
      { property: "og:title", content: "CARIO source registry" },
      { property: "og:description", content: "The full catalogue of public OSINT endpoints, feeds and datasets behind CARIO investigations." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: SourcesPage,
});

const STATUS_TONE: Record<string, string> = {
  ACTIVE: "text-success",
  DEGRADED: "text-amber",
  RATE_LIMITED: "text-amber",
  AUTH_REQUIRED: "text-muted-foreground",
  TEMPORARILY_UNAVAILABLE: "text-danger",
  DEPRECATED: "text-danger",
  DISABLED: "text-muted-foreground",
  DISCOVERED: "text-cyan",
};

function SourcesPage() {
  const [tab, setTab] = useState<"registry" | "collectors">("registry");
  const [q, setQ] = useState("");
  const [pack, setPack] = useState("all");
  const [status, setStatus] = useState("all");
  const [offset, setOffset] = useState(0);

  const overviewFn = useServerFn(getFabricOverview);
  const validateFn = useServerFn(validateFabricSources);
  const legacyFn = useServerFn(fetchSourceHealth);

  const { data, isFetching, refetch } = useQuery({ queryKey: ["fabric-overview"], queryFn: () => overviewFn(), staleTime: 30_000 });
  const legacy = useQuery({ queryKey: ["source-health"], queryFn: () => legacyFn(), enabled: tab === "collectors", staleTime: 60_000 });

  const validate = useMutation({
    mutationFn: async () => validateFn({ data: { offset, limit: 60 } }),
    onSuccess: () => {
      setOffset((o) => o + 60);
      void refetch();
    },
  });

  const rows = data?.sources ?? [];
  const packs = useMemo(() => Array.from(new Set(rows.map((r) => r.pack))).sort(), [rows]);
  const filtered = rows.filter(
    (r) =>
      (pack === "all" || r.pack === pack) &&
      (status === "all" || r.status === status) &&
      `${r.name} ${r.provider} ${r.category} ${r.country ?? ""}`.toLowerCase().includes(q.toLowerCase()),
  );

  return (
    <AppLayout>
      <ModuleShell
        eyebrow="Source Registry"
        title="Source fabric control plane"
        description="Every source definition CARIO can query — provider, protocol, country, access class and live validation state. Searches route only the relevant slice, never the whole registry."
        meta={
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => refetch()}
              className="inline-flex items-center gap-2 rounded-md border border-panel-border bg-panel/40 px-3 py-1.5 text-xs"
            >
              <RefreshCw className={`h-3 w-3 ${isFetching ? "animate-spin" : ""}`} />
              Refresh
            </button>
            <button
              onClick={() => validate.mutate()}
              disabled={validate.isPending}
              className="inline-flex items-center gap-2 rounded-md border border-panel-border bg-panel/40 px-3 py-1.5 text-xs disabled:opacity-50"
            >
              <ShieldCheck className={`h-3 w-3 ${validate.isPending ? "animate-pulse" : ""}`} />
              {validate.isPending ? "Validating…" : `Validate next 60 (from #${offset})`}
            </button>
          </div>
        }
      >
        <div className="flex gap-2">
          {(["registry", "collectors"] as const).map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`rounded-md border px-3 py-1.5 text-xs ${tab === t ? "border-cyan/60 bg-cyan/10 text-cyan" : "border-panel-border bg-panel/30 text-muted-foreground"}`}
            >
              {t === "registry" ? "Source registry" : "Legacy collector probes"}
            </button>
          ))}
        </div>

        {tab === "registry" ? (
          <>
            <div className="grid gap-3 md:grid-cols-4">
              <StatTile label="Registered definitions" value={data?.registry.total ?? 0} />
              <StatTile label="Validated" value={data?.validated ?? 0} tone="success" />
              <StatTile label="Active" value={data?.active ?? 0} tone="success" />
              <StatTile label="Needs credentials" value={data?.registry.authRequired ?? 0} tone="amber" />
            </div>
            <div className="grid gap-3 md:grid-cols-4">
              <StatTile label="Free & keyless" value={data?.registry.keyless ?? 0} />
              <StatTile label="Degraded" value={data?.degraded ?? 0} tone="amber" />
              <StatTile label="Unreachable" value={(data?.unavailable ?? 0) + (data?.deprecated ?? 0)} tone="amber" />
              <StatTile label="Countries" value={data?.registry.countries ?? 0} />
            </div>

            {validate.data ? (
              <div className="glass rounded-xl p-3 text-xs text-muted-foreground">
                Last validation run checked {validate.data.checked} sources —{" "}
                {Object.entries(validate.data.tally)
                  .map(([k, v]) => `${k}: ${v}`)
                  .join(" · ")}
              </div>
            ) : null}

            <div className="glass rounded-xl p-3">
              <div className="flex flex-col gap-2 md:flex-row md:items-center">
                <div className="flex flex-1 items-center gap-2 rounded-md border border-panel-border bg-background/40 px-3 py-2">
                  <Search className="h-4 w-4 text-cyan" />
                  <input
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder="Search by source, provider, category or country…"
                    className="flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
                  />
                </div>
                <select
                  value={pack}
                  onChange={(e) => setPack(e.target.value)}
                  className="rounded-md border border-panel-border bg-background/40 px-2 py-2 text-xs"
                >
                  <option value="all">All packs</option>
                  {packs.map((p) => (
                    <option key={p} value={p}>
                      {p.replace(/_/g, " ")}
                    </option>
                  ))}
                </select>
                <select
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  className="rounded-md border border-panel-border bg-background/40 px-2 py-2 text-xs"
                >
                  {["all", "ACTIVE", "DISCOVERED", "DEGRADED", "RATE_LIMITED", "AUTH_REQUIRED", "TEMPORARILY_UNAVAILABLE", "DEPRECATED", "DISABLED"].map((s) => (
                    <option key={s} value={s}>
                      {s === "all" ? "Any state" : s.replace(/_/g, " ")}
                    </option>
                  ))}
                </select>
              </div>

              <p className="mt-2 text-[11px] text-muted-foreground">
                Showing {filtered.length} of {rows.length} definitions.
              </p>

              <div className="mt-3 overflow-hidden rounded-md border border-panel-border">
                <div className="grid grid-cols-12 gap-2 border-b border-panel-border bg-panel/40 px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                  <div className="col-span-4">Source</div>
                  <div className="col-span-2">Pack</div>
                  <div className="col-span-2">Protocol</div>
                  <div className="col-span-1">Cost</div>
                  <div className="col-span-1">HTTP</div>
                  <div className="col-span-2">State</div>
                </div>
                <div className="max-h-[60vh] overflow-y-auto">
                  {filtered.slice(0, 600).map((r) => (
                    <div key={r.sourceId} className="grid grid-cols-12 gap-2 px-3 py-2 text-xs hover:bg-panel/50">
                      <div className="col-span-4 flex items-center gap-2">
                        <Database className="h-3.5 w-3.5 shrink-0 text-cyan" />
                        <span className="truncate" title={`${r.provider} — ${r.category}`}>
                          {r.name}
                        </span>
                      </div>
                      <div className="col-span-2 truncate text-muted-foreground">
                        {r.pack.replace(/_/g, " ")}
                        {r.country ? ` · ${r.country}` : ""}
                      </div>
                      <div className="col-span-2 font-mono text-muted-foreground">{r.sourceType}</div>
                      <div className="col-span-1 font-mono text-[10px] text-muted-foreground">
                        {r.costClass === "FREE_KEYLESS" ? "free" : r.costClass === "FREE_KEY" ? <KeyRound className="h-3 w-3" /> : r.costClass.toLowerCase()}
                      </div>
                      <div className="col-span-1 font-mono text-cyan">{r.httpStatus ?? "—"}</div>
                      <div className={`col-span-2 flex items-center gap-1 font-mono ${STATUS_TONE[r.status] ?? "text-muted-foreground"}`}>
                        <Activity className="h-3 w-3" />
                        <span className="truncate">{r.status.replace(/_/g, " ")}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="glass rounded-xl p-3">
              <h3 className="mb-2 text-xs font-mono uppercase tracking-widest text-muted-foreground">Source packs</h3>
              <div className="flex flex-wrap gap-2">
                {(data?.registry.byPack ?? []).map((p) => (
                  <button
                    key={p.pack}
                    onClick={() => setPack(p.pack)}
                    className="rounded-md border border-panel-border bg-panel/30 px-2 py-1 text-[11px] text-muted-foreground hover:text-foreground"
                  >
                    {p.pack.replace(/_/g, " ")} <span className="font-mono text-cyan">{p.count}</span>
                  </button>
                ))}
              </div>
            </div>
          </>
        ) : (
          <div className="glass rounded-xl p-3">
            <div className="overflow-hidden rounded-md border border-panel-border">
              <div className="grid grid-cols-12 gap-2 border-b border-panel-border bg-panel/40 px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                <div className="col-span-5">Collector</div>
                <div className="col-span-3">Category</div>
                <div className="col-span-2">Latency</div>
                <div className="col-span-2">State</div>
              </div>
              {(legacy.data ?? []).map((r) => (
                <div key={r.name} className="grid grid-cols-12 gap-2 px-3 py-2 text-xs">
                  <div className="col-span-5 truncate">{r.name}</div>
                  <div className="col-span-3 text-muted-foreground">{r.category}</div>
                  <div className="col-span-2 font-mono text-muted-foreground">{r.latency}ms</div>
                  <div className={`col-span-2 font-mono ${r.status === "online" ? "text-success" : r.status === "degraded" ? "text-amber" : "text-danger"}`}>
                    {r.status.toUpperCase()}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </ModuleShell>
    </AppLayout>
  );
}
